import { Navigate, useLocation } from 'react-router-dom'
import { evaluateUrl } from '@/lib/launchGating'
import { useEntitlements } from '@/hooks/useEntitlements'
import { useTrialState } from '@/hooks/useDeploy'
import { useAuthStore } from '@/stores/authStore'
import { UpgradePrompt } from '@/components/launch/UpgradePrompt'

/**
 * Launch gate (Plan 33 / RR-5): block direct-URL access to FLAG_OFF pages, not
 * just hide them from the nav. Every protected/admin page renders through the two
 * helpers below, so checking here covers all gated routes in one place. Redirects
 * to /dashboard (always a SHIP-surface page) when the current URL is flagged off.
 */
export function LaunchGate({ children }: { children: React.ReactElement }) {
  const { pathname } = useLocation()
  // Evaluate with the LIVE entitlements (reactive), not launchGating's effect-synced module
  // snapshot — otherwise a cold load / hard refresh of a tier-gated page renders the upgrade wall
  // before <LaunchEntitlementsSync>'s effect writes the tier, and never re-renders to correct it.
  const ent = useEntitlements()
  const isAuthed = useAuthStore((s) => !!s.user)
  const { isLoading: planLoading } = useTrialState(isAuthed)
  const { result, tier } = evaluateUrl(pathname, ent.tier, ent.isPlatformAdmin)
  // Don't flash an upgrade wall before the user's plan is known — wait for trial-state to resolve.
  if (result === 'needs_upgrade' && isAuthed && planLoading) return null
  // Shipped but the plan tier is too low → show an upgrade path (RR-5) instead
  // of a silent redirect. Not-yet-launched features stay hidden (redirect).
  if (result === 'needs_upgrade') return <UpgradePrompt requiredTier={tier} />
  if (result === 'not_shipped') return <Navigate to="/dashboard" replace />
  return children
}
